import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from "react";
import styled from "styled-components";
import { visuallyHidden } from "@m/alchemy-theme";
import { useSlateRTE } from "../../SlateRTEContext";
import { LoadingIndicator } from "../../../../progress/loading-indicator/LoadingIndicator";
import { GridIcon } from "../../../../icons/GridIcon";
import { LinkIcon } from "../../../../icons/LinkIcon";
import { UploadIcon } from "../../../../icons/UploadIcon";
import { FocusList } from "../../../../utilities/focus-list/FocusList";
import { PopperBox } from "../../../../popper/PopperBox";
import { MenuList } from "../../../../menu-list/MenuList";
import { MenuListItem } from "../../../../menu-list-item/index";
import { useUniqueId } from "../../../../hooks/useUniqueId";
import { insertImage } from "./withImages";
import { ImageUrlDialog } from "./ImageUrlDialog";
const StyledFileInput = styled.input.attrs({ type: "file" }) `
  cursor: inherit;
  opacity: 0;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
`;
const StyledUploadWrapper = styled.span `
  ${visuallyHidden()};
`;
export const ImageMenu = ({ editor, close, closeAndFocusEditor, options = {
    upload: true,
    url: true,
    browse: false
} }) => {
    const fileInput = useRef(null);
    const uploadItemId = useUniqueId("upload-item");
    const [isLoading, setIsLoading] = useState(false);
    const [isBrowseDialogOpen, setIsBrowseDialogOpen] = useState(false);
    const [isImageUrlDialogOpen, setIsImageUrlDialogOpen] = useState(false);
    const { stringMap } = useSlateRTE();
    // Replace booleans with full config and merge options with defaults.
    const config = {
        upload: {
            label: stringMap.uploadImageMenuLabel,
            onUpload: defaultOnUpload,
            ...(options.upload === true ? null : options.upload)
        },
        url: { label: stringMap.insertImageFromUrlMenuLabel, ...(options.url === true ? null : options.url) },
        browse: { label: stringMap.browseImageMenuLabel, ...options.browse }
    };
    const insertImageUrl = (imageUrl, alt = "") => insertImage(editor, imageUrl, alt);
    return (_jsx(PopperBox, { hasPadding: false, children: _jsx(FocusList, { itemSelector: `[role="menuitem"]`, keyboardEventMustMatchItemSelector: true, keyboardDirection: "vertical", groupingSelector: "[data-menu-list-item]", children: _jsxs(MenuList, { mode: "menu", children: [options.upload && (_jsx(MenuListItem, { id: uploadItemId, icon: _jsx(UploadIcon, { "aria-hidden": "true" }), disabled: isLoading, label: isLoading ? (_jsx(LoadingIndicator, { "aria-label": stringMap.loadingImage })) : (config.upload.label || stringMap.uploadImageMenuLabel), onClick: () => {
                            fileInput.current?.click();
                        }, endItems: _jsx(StyledUploadWrapper, { children: _jsx(StyledFileInput, { tabIndex: -1, "aria-labelledby": uploadItemId, "data-testid": "SlateRTEImageUploadInput", ref: fileInput, accept: "image/*", onChange: async (event) => {
                                    const { target } = event;
                                    if (target.files && target.files.length > 0) {
                                        const file = target.files[0];
                                        setIsLoading(true);
                                        const { url, alt } = await config.upload.onUpload(file);
                                        insertImageUrl(url, alt);
                                        setIsLoading(false);
                                        closeAndFocusEditor();
                                    }
                                } }) }) })), options.url && (_jsxs(_Fragment, { children: [_jsx(MenuListItem, { icon: _jsx(LinkIcon, { "aria-hidden": "true" }), label: config.url.label || stringMap.insertImageFromUrlMenuLabel, onClick: () => {
                                    setIsImageUrlDialogOpen(true);
                                } }), _jsx(ImageUrlDialog
                            // TODO: fix keyboard behaviour, should restore focus to toolbar on cancel and to editor on insert
                            , { 
                                // TODO: fix keyboard behaviour, should restore focus to toolbar on cancel and to editor on insert
                                onInsert: () => {
                                    setIsImageUrlDialogOpen(false);
                                }, onPostDeactivate: closeAndFocusEditor, onClose: () => {
                                    setIsImageUrlDialogOpen(false);
                                    closeAndFocusEditor();
                                }, isOpen: isImageUrlDialogOpen, insertImageUrl: insertImageUrl })] })), options.browse && (_jsxs(_Fragment, { children: [_jsx(MenuListItem, { icon: _jsx(GridIcon, { "aria-hidden": "true" }), label: options.browse.label || stringMap.browseImageMenuLabel, onClick: () => {
                                    setIsBrowseDialogOpen(true);
                                } }), config.browse.renderDialog?.({
                                closeMenu: close,
                                close: () => setIsBrowseDialogOpen(false),
                                isOpen: isBrowseDialogOpen,
                                insertImage: insertImageUrl
                            })] }))] }) }) }));
};
const defaultOnUpload = (file) => new Promise((resolve) => {
    resolve({ url: URL.createObjectURL(file), alt: file.name });
});
